import { computed } from 'vue'

import type { ContentItem, MetricRow } from '../content-item-details.types'

import { useDetailFormatters } from './useDetailFormatters'

export const useContentItemMetrics = (getActiveContentItem: () => ContentItem | undefined) => {
  const { formatInteger, formatPercent, formatMoney } = useDetailFormatters()

  const metrics = computed<MetricRow[] | null>(() => {
    const contentItem = getActiveContentItem()
    if (!contentItem?.metrics) {
      return null
    }

    const {
      views,
      estimatedViews,
      uniqueViews,
      estimatedUniqueViews,
      estimatedEngagementRate,
      engagementRate,
      mediaValue,
      estimatedMediaValue,
      clickThroughRate,
      estimatedClickThroughRate,
    } = contentItem.metrics

    const rows: MetricRow[] = []
    if (views != null) {
      rows.push({ label: 'Views', value: formatInteger(views, true) })
    }
    if (estimatedViews != null) {
      rows.push({
        label: 'Est. views',
        value: formatInteger(estimatedViews, true),
        tooltip: {
          title: 'Estimated views',
          description: "Estimated based on creator's performance.",
        },
      })
    }
    if (uniqueViews != null) {
      rows.push({ label: 'Unique views', value: formatInteger(uniqueViews, true) })
    }
    if (estimatedUniqueViews != null) {
      rows.push({
        label: 'Est. unique views',
        value: formatInteger(estimatedUniqueViews, true),
        tooltip: {
          title: 'Estimated unique views',
          description:
            "Estimated based on creator's performance.\nAlso known as 'Reach' by Instagram.",
        },
      })
    }
    if (clickThroughRate != null) {
      rows.push({
        label: 'Click-through rate',
        value: formatPercent(clickThroughRate, true),
        tooltip: {
          title: 'Click-through rate',
          description: 'Percentage of clicks out of total views.',
        },
      })
    }
    if (estimatedClickThroughRate != null) {
      rows.push({
        label: 'Est. click-through rate',
        value: formatPercent(estimatedClickThroughRate, true),
      })
    }
    if (engagementRate != null) {
      rows.push({ label: 'Engagement rate', value: formatPercent(engagementRate, true) })
    }
    if (estimatedEngagementRate != null) {
      rows.push({
        label: 'Est. engagement rate',
        value: formatPercent(estimatedEngagementRate, true),
      })
    }
    if (mediaValue != null) {
      rows.push({ label: 'Media value', value: formatMoney(mediaValue) })
    }
    if (estimatedMediaValue != null) {
      rows.push({
        label: 'Est. media value',
        value: formatMoney(estimatedMediaValue),
        tooltip: {
          title: 'Estimated media value',
          description:
            "The estimated monetary worth of your content's exposure.\n(Est. Views/1000)*CPM",
        },
      })
    }

    return rows.length > 0 ? rows : null
  })

  return { metrics }
}
